import React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import { Action } from "typescript-fsa";
import { Map } from "immutable";
import log from "loglevel";

import { WaIcon } from "@ui/WaIcon";
import { Spinner } from "@ui/Spinner";
import { ApiRequest } from "../../requests";
import { getAllRequests, makeGetRequestsLastLoaded } from "../../selectors";
import { ApiState } from "../../models/state";

// Props for the HOC
type LoadingStyle = "block" | "spinner";

export interface ApiConnectedOptions {
    actionCreator?: ActionCreator;
    actionCreators?: ActionCreator[];
    actionCreatorProps?: ActionCreatorProps[];
    lastLoaded?: Date[];
    showRefresh?: boolean;
    style?: LoadingStyle;
    loadOnMount?: boolean;
    reloadOnMount?: boolean;
    cardPage?: boolean;
}

export type ApiConnectedOptionsFunc = (props: any) => ApiConnectedOptions;

type Options = ApiConnectedOptions | ApiConnectedOptionsFunc;

function isOptionsFunc(opts: Options): opts is ApiConnectedOptionsFunc {
    return typeof opts === "function";
}

// Props for the ApiConnectedComponent
type ActionCreatorProps = any[];

export type ActionCreator = (...args: any[]) => Action<ApiRequest>;

type ActionCreatorWithProps = (props: ActionCreatorProps) => Action<ApiRequest>;

export interface UpdateActionProps {
    updateActionProps?: (props: ActionCreatorProps[]) => void;
}

export interface ApiInjectedProps {
    call?: (index: number) => Promise<any>;
}

interface ApiConnectedStateProps {
    requests: Map<string, ApiRequest>;
    lastLoaded?: Date[];
}

interface ApiConnectedDispatchProps {
    dispatchActionCreators: ActionCreatorWithProps[];
}

export interface ApiConnectedOwnProps {
    actionCreators?: ActionCreator[];
    lastLoaded?: Date[];
    // Update this to force a reload.
    requestId?: string[];
}

export interface ApiConnectedProps extends ApiConnectedOwnProps,
    ApiConnectedStateProps, ApiConnectedDispatchProps, ApiInjectedProps {
}

export interface ApiConnectedState {
    loading: boolean;
    refreshing: boolean;
    currentRequests: ApiRequest[];
    resolves: Array<(value?: any) => void>;
    rejects: Array<(reason?: Error) => void>;
}

export const connectToApi = (opts: Options) =>
    <TProps extends UpdateActionProps & ApiConnectedProps>(
        WrappedComponent: (React.ComponentClass<TProps>
            | React.StatelessComponent<TProps>),
    ) => {
        class ApiConnectedComponent extends React.Component<TProps, ApiConnectedState> {
            private actionCreatorProps: ActionCreatorProps[] = null;
            private options: ApiConnectedOptions = null;

            constructor(props: TProps) {
                super(props);

                if (isOptionsFunc(opts)) {
                    this.options = opts(props);
                }
                else {
                    this.options = opts;
                }

                // Set the default options
                this.options = Object.assign({
                    actionCreatorProps: null,
                    lastLoaded: null,
                    showRefresh: true,
                    style: "spinner",
                    loadOnMount: true,
                    reloadOnMount: false,
                }, this.options);

                // Initialise the state.
                this.state = {
                    currentRequests: [],
                    loading: false,
                    refreshing: false,
                    resolves: [],
                    rejects: [],
                };
                // Set the action creator props.
                this.actionCreatorProps = this.options.actionCreatorProps;
                const actionCreators = this.getActionCreators();
                /* istanbul ignore else */
                if (this.actionCreatorProps == null) {
                    this.actionCreatorProps = (new Array(actionCreators.length)).fill([]);
                }
            }

            private getActionCreators() {
                if (this.options.actionCreators != null) {
                    return this.options.actionCreators;
                }
                else if (this.options.actionCreator) {
                    return [this.options.actionCreator];
                }
                else if (this.props.actionCreators != null) {
                    return this.props.actionCreators;
                }
                return [];
            }

            private getLastLoaded() {
                return this.options.lastLoaded != null ?
                    this.options.lastLoaded : this.props.lastLoaded;
            }

            public componentWillMount() {
                // Don't load if we're not supposed to.
                if (!this.options.loadOnMount) {
                    return;
                }

                if (this.options.lastLoaded == null && this.props.lastLoaded == null) {
                    log.error("You must supply `lastLoaded` when connecting to the api. " +
                        "Either through props or the options.");
                    return;
                }
                const lastLoaded = this.getLastLoaded();

                // Fire off the action creators if they've not been loaded.
                const { dispatchActionCreators } = this.props;
                const requests: ApiRequest[] = new Array(dispatchActionCreators.length);
                let requestsStarted = false;
                let firstLoaded = false;

                for (const i in lastLoaded) {
                    if (dispatchActionCreators[i] !== undefined) {
                        const requestLastLoaded = lastLoaded[i];
                        const actionCreator = dispatchActionCreators[i];

                        // If it's never been loaded, perform the request.
                        if (requestLastLoaded == null || this.options.reloadOnMount) {
                            if (requestLastLoaded == null) {
                                firstLoaded = true;
                            }
                            const action = actionCreator(this.actionCreatorProps[i]);
                            requests[i] = action.payload;
                            requestsStarted = true;
                        }
                    }
                }

                if (requestsStarted) {
                    this.setState({
                        currentRequests: requests,
                        loading: firstLoaded,
                        refreshing: !firstLoaded,
                    });
                }
            }

            public componentWillReceiveProps(nextProps: Readonly<TProps>) {
                // Check if one of the requests has updated
                const { currentRequests, resolves, rejects } = this.state;
                let atLeastOneLoading = false;

                for (let i = 0; i < currentRequests.length; i++) {
                    const currentRequest = currentRequests[i];

                    if (currentRequest != null) {
                        const nextRequest = nextProps.requests.get(currentRequest.uuid);
                        // If the request exists.
                        if (nextRequest != null) {
                            // Update the requests with this one.
                            currentRequests[i] = nextRequest;
                            // Set loading if in progress.
                            if (nextRequest.isInProgress()) {
                                atLeastOneLoading = true;
                            }
                            else if (nextRequest.isFinishedSucceeded() && resolves[i] != null) {
                                resolves[i](nextRequest.response);
                                resolves[i] = null;
                                rejects[i] = null;
                            }
                            else if (nextRequest.isFinishedFailed() && rejects[i] != null) {
                                rejects[i](new Error(nextRequest.error.message));
                                resolves[i] = null;
                                rejects[i] = null;
                            }
                        }
                    }
                }

                // Check if any requests have changed.
                const actionCreators = getActionCreatorsFromOptionsProps(this.options, nextProps);
                if (this.props.requestId != null && nextProps.requestId != null) {
                    for (const i in actionCreators) {
                        if (this.props.requestId[i] !== nextProps.requestId[i]) {
                            // Rerun if the request ID is different.
                            const actionCreator = nextProps.dispatchActionCreators[i];
                            const action = actionCreator(this.actionCreatorProps[i]);
                            currentRequests[i] = action.payload;
                            atLeastOneLoading = true;
                        }
                    }
                }

                this.setState((prevState: ApiConnectedState) => {
                    return {
                        currentRequests,
                        resolves,
                        rejects,
                        loading: prevState.loading && atLeastOneLoading,
                        refreshing: !prevState.loading && atLeastOneLoading,
                    };
                });
            }

            private refresh(event: React.MouseEvent<HTMLAnchorElement>) {
                event.preventDefault();

                const { dispatchActionCreators } = this.props;
                const { currentRequests, refreshing, loading } = this.state;
                if (!refreshing && !loading && dispatchActionCreators.length > 0) {
                    // Start as 'refreshing' before we actually start the requests.
                    this.setState({
                        refreshing: true,
                    });

                    // Start the requests again.
                    for (let i = 0; i < dispatchActionCreators.length; i++) {
                        const action = dispatchActionCreators[i](this.actionCreatorProps[i]);
                        currentRequests[i] = action.payload;
                    }

                    // Update the requests too
                    this.setState({
                        currentRequests,
                        refreshing: true,
                    });
                }
            }

            private call(index: number): Promise<any> {
                const { dispatchActionCreators } = this.props;
                const { currentRequests, resolves, rejects } = this.state;

                if (dispatchActionCreators[index] == null) {
                    return null;
                }

                // Start as 'refreshing' before we actually start the request.
                this.setState({
                    refreshing: true,
                });

                const self = this;
                return new Promise(
                    (resolve: (value?: any) => void, reject: (reason?: any) => void) => {
                        const action = dispatchActionCreators[index](this.actionCreatorProps[index]);
                        currentRequests[index] = action.payload;
                        resolves[index] = resolve;
                        rejects[index] = reject;

                        // Set loading only if we get a request
                        self.setState({
                            refreshing: true,
                            currentRequests,
                            resolves,
                            rejects,
                        });
                    },
                );
            }

            public render(): JSX.Element {
                let contents: React.ReactNode = (
                    <WrappedComponent key="contents" {...this.props}
                        updateActionProps={
                            (props: any[][]) => {
                                this.actionCreatorProps = props;
                            }
                        }
                        call={(index: number) => this.call(index)}
                    />
                );

                // Add the refresh button at the top.
                if (this.options.showRefresh) {
                    let refreshButton = (
                        <a href="#" onClick={this.refresh.bind(this)}>
                            <WaIcon name="refresh" />
                        </a>
                    );
                    if (this.state.refreshing) {
                        refreshButton = <Spinner inline />;
                    }
                    const refresh = (
                        <div key="refresh" className="api-component__refresh">
                            {refreshButton}
                        </div>
                    );
                    contents = [ refresh, contents ];
                }

                if (this.state.loading) {
                    if (this.options.style === "block") {
                        contents = <div className="loader">Loading...</div>;
                    }
                    else {
                        contents = [
                            contents,
                            <div key="spinner" className="api-component__overlay">
                                <Spinner size="3x" />
                            </div>,
                        ];
                    }
                }
                const className = "api-component" + (this.options.cardPage ? " api-component--card-page" : "");
                return (
                    <div className={className}>
                        {contents}
                    </div>
                );
            }
        }

        const makeMapStateToProps = () => {
            const getRequestsLastLoaded = makeGetRequestsLastLoaded();
            return (state: ApiState, ownProps: TProps) => {
                const props = {
                    requests: getAllRequests(state),
                } as ApiConnectedStateProps;
                // Get lastLoaded from the store if it's not on the props.
                const lastLoaded = getLastLoadedFromOptionsProps(opts, ownProps);
                if (lastLoaded == null) {
                    const actionCreators = getActionCreatorsFromOptionsProps(opts, ownProps);
                    props.lastLoaded = getRequestsLastLoaded(state, { actionCreators });
                }
                return props;
            };
        };

        const mapDispatchToProps = (dispatch: Dispatch<ApiState>, props: TProps) => {
            const actionCreators = getActionCreatorsFromOptionsProps(opts, props);

            return {
                dispatchActionCreators: actionCreators.map(
                    (actionCreator) => (prps: ActionCreatorProps = []) => {
                        const action = actionCreator(...prps);
                        dispatch(action);
                        return action;
                    }),
            } as ApiConnectedDispatchProps;
        };

        const getActionCreatorsFromOptionsProps = (ops: Options, props: TProps) => {
            let actionCreators: ActionCreator[] = [];
            if (isOptionsFunc(ops)) {
                const options = ops(props);
                if (options.actionCreators != null) {
                    actionCreators = options.actionCreators;
                }
                else if (options.actionCreator != null) {
                    actionCreators = [options.actionCreator];
                }
            }
            else if (ops.actionCreators != null) {
                actionCreators = ops.actionCreators;
            }
            else if (ops.actionCreator != null) {
                actionCreators = [ops.actionCreator];
            }
            if (props != null && props.actionCreators != null) {
                actionCreators = props.actionCreators;
            }
            return actionCreators;
        };

        const getLastLoadedFromOptionsProps = (ops: Options, props: TProps) => {
            let lastLoaded: Date[] = null;
            if (isOptionsFunc(ops)) {
                const options = ops(props);
                if (options.lastLoaded != null) {
                    lastLoaded = options.lastLoaded;
                }
            }
            else if (ops.lastLoaded != null) {
                lastLoaded = ops.lastLoaded;
            }
            if (props != null && props.lastLoaded != null) {
                lastLoaded = props.lastLoaded;
            }
            return lastLoaded;
        };

        return connect(makeMapStateToProps, mapDispatchToProps)(ApiConnectedComponent);
    };
